import { useState, useRef, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface ClockPickerProps {
  value: string;
  onChange: (time: string) => void;
  onComplete?: () => void;
}

type ClockMode = 'hour' | 'minute';
type Period = 'AM' | 'PM';

export const parseTimeValue = (timeStr: string) => {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)?$/i);
  if (match) {
    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    const explicitPeriod = match[3]?.toUpperCase() as Period | undefined;
    let period: Period = explicitPeriod || 'AM';

    if (!explicitPeriod) {
      // No AM/PM in the input ⇒ it was 24-hour; derive the 12-hour clock value.
      if (hour === 0) {
        hour = 12;
        period = 'AM';
      } else if (hour === 12) {
        period = 'PM';
      } else if (hour > 12) {
        hour -= 12;
        period = 'PM';
      } else {
        period = 'AM';
      }
    }

    return { hour, minute, period };
  }
  return { hour: 12, minute: 0, period: 'AM' as Period };
};

export const ClockPicker: React.FC<ClockPickerProps> = ({ value, onChange, onComplete }) => {
  const initialTime = parseTimeValue(value);
  const [mode, setMode] = useState<ClockMode>('hour');
  const [hour, setHour] = useState<number>(initialTime.hour);
  const [minute, setMinute] = useState<number>(initialTime.minute);
  const [period, setPeriod] = useState<Period>(initialTime.period);
  const clockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const parsed = parseTimeValue(value);
      if (parsed.hour !== hour || parsed.minute !== minute || parsed.period !== period) {
        setHour(parsed.hour);
        setMinute(parsed.minute);
        setPeriod(parsed.period);
      }
    }
  }, [value, hour, minute, period]);

  const handleHourChange = (h: number) => {
    setHour(h);
    const formattedTime = `${h}:${minute.toString().padStart(2, '0')} ${period}`;
    onChange(formattedTime);
    setMode('minute');
  };

  const handleMinuteChange = (m: number) => {
    setMinute(m);
    const formattedTime = `${hour}:${m.toString().padStart(2, '0')} ${period}`;
    onChange(formattedTime);
    if (onComplete) {
      onComplete();
    }
  };

  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    const formattedTime = `${hour}:${minute.toString().padStart(2, '0')} ${p}`;
    onChange(formattedTime);
  };

  const renderClockNumbers = () => {
    const numbers = mode === 'hour' ? Array.from({ length: 12 }, (_, i) => i + 1) : Array.from({ length: 12 }, (_, i) => i * 5);
    const radius = 90;
    const centerX = 120;
    const centerY = 120;

    return numbers.map((num) => {
      let angle;
      if (mode === 'hour') {
        angle = (90 - num * 30) * (Math.PI / 180);
      } else {
        angle = (90 - num * 6) * (Math.PI / 180);
      }

      const x = centerX + radius * Math.cos(angle);
      const y = centerY - radius * Math.sin(angle);

      const isSelected = mode === 'hour' ? num === hour : num === minute;

      return (
        <button
          key={num}
          type="button"
          onClick={() => mode === 'hour' ? handleHourChange(num) : handleMinuteChange(num)}
          className={`absolute w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
            ${isSelected
              ? 'bg-blue-500 text-white scale-110'
              : 'text-gray-700 hover:bg-blue-50'
            }`}
          style={{
            left: `${x - 16}px`,
            top: `${y - 16}px`,
          }}
        >
          {num === 0 ? '00' : num}
        </button>
      );
    });
  };

  const renderClockHand = () => {
    const value = mode === 'hour' ? hour : minute;
    const radius = 90;
    const centerX = 120;
    const centerY = 120;

    let clockAngle;
    if (mode === 'hour') {
      clockAngle = value * 30;
    } else {
      clockAngle = value * 6;
    }

    return (
      <>
        <div
          className="absolute bg-blue-500"
          style={{
            left: `${centerX - 1}px`,
            top: `${centerY - radius}px`,
            width: '2px',
            height: `${radius}px`,
            transformOrigin: '1px 100%',
            transform: `rotate(${clockAngle}deg)`,
          }}
        />
        <div
          className="absolute w-3 h-3 bg-blue-500 rounded-full"
          style={{
            left: `${centerX - 6}px`,
            top: `${centerY - 6}px`,
          }}
        />
      </>
    );
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4 px-4">
        <button
          type="button"
          onClick={() => setMode(mode === 'hour' ? 'minute' : 'hour')}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <FiChevronLeft size={20} />
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMode('hour')}
            className={`text-2xl font-medium px-2 py-1 rounded transition-colors ${
              mode === 'hour' ? 'text-blue-500 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {hour.toString().padStart(2, '0')}
          </button>
          <span className="text-2xl font-medium text-gray-400">:</span>
          <button
            type="button"
            onClick={() => setMode('minute')}
            className={`text-2xl font-medium px-2 py-1 rounded transition-colors ${
              mode === 'minute' ? 'text-blue-500 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {minute.toString().padStart(2, '0')}
          </button>
        </div>

        <button
          type="button"
          onClick={() => setMode(mode === 'hour' ? 'minute' : 'hour')}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <FiChevronRight size={20} />
        </button>
      </div>

      <div className="relative mx-auto mb-4" style={{ width: '240px', height: '240px' }}>
        <div className="absolute inset-0 rounded-full border-2 border-gray-200 bg-gray-50" />
        {renderClockHand()}
        {renderClockNumbers()}
      </div>

      <div className="flex justify-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => handlePeriodChange('AM')}
          className={`px-6 py-2 rounded-md font-medium transition-colors ${
            period === 'AM'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          AM
        </button>
        <button
          type="button"
          onClick={() => handlePeriodChange('PM')}
          className={`px-6 py-2 rounded-md font-medium transition-colors ${
            period === 'PM'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          PM
        </button>
      </div>
    </div>
  );
};
